<template>
  <!-- The async-setup screen is swapped in at the root once the org probe
       resolves; a stable root element keeps the route <Suspense> anchor
       valid (see .stable-route-root in global.scss). -->
  <div class="stable-route-root">
    <div v-if="entitlementBlocked" />
    <NotFound
      v-else-if="notFound"
      description="This automation does not exist or you do not have access to it."
    />
    <AutomationScreen
      v-else-if="resolvedOrgId"
      :automation-id="objectId"
      :organization-id="resolvedOrgId"
      :show-top-bar="showTopBar"
      :initial-run-id="initialRunId"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from "vue";
import { useRoute } from "vue-router";

import type { OrgId } from "@ctrl/controller-rpc";

import AutomationScreen from "@/components/ai/Automation.vue";
import * as inject from "@/components/inject";
import { openUpgradeModal } from "@/components/modals/upgrade";
import NotFound from "@/components/NotFound.vue";
import { waitForEntitlement } from "@/utils/billing";

const ENTITLEMENT_KEY = "ai-automations";

export default defineComponent({
  name: "AutomationRoute",
  components: { AutomationScreen, NotFound },
  props: {
    objectId: { type: String, required: true },
    organizationId: { type: String, default: null },
    showTopBar: { type: Boolean, default: true },
  },
  setup(props) {
    const route = useRoute();
    const billing = inject.billing();
    const controller = inject.controller();
    const profile = inject.profile();

    // Inbox notices and other deep links often carry a raw `automation:…` id
    // with no organization. Probe like AutomationRunRoute (FRONTEND-FH5).
    const explicitOrgId = computed(
      () =>
        props.organizationId ||
        (typeof route.query.organizationId === "string"
          ? route.query.organizationId
          : null)
    );
    const resolvedOrgId = ref<OrgId | null>(null);
    const notFound = ref(false);
    const entitlementBlocked = ref(false);

    let probe = 0;
    watch(
      () => [props.objectId, explicitOrgId.value] as const,
      async ([automationId, explicit]) => {
        const current = ++probe;
        notFound.value = false;
        if (explicit) {
          resolvedOrgId.value = explicit;
          return;
        }
        resolvedOrgId.value = null;
        for (const organization of profile.value.organizations) {
          const automationIds =
            (await controller.ai.automation.all(organization.id)).release() ??
            [];
          if (current !== probe) return;
          if (automationIds.includes(automationId)) {
            resolvedOrgId.value = organization.id;
            return;
          }
        }
        notFound.value = true;
      },
      { immediate: true }
    );

    watch(
      () => [props.objectId, resolvedOrgId.value, billing.value] as const,
      async ([, org]) => {
        if (!org) {
          entitlementBlocked.value = false;
          return;
        }
        // Account-level: any org's plan grants automations. Do not scope to
        // the automation's org (assistant automations live in the personal
        // org, which stays Free when a team org is on Business).
        // Waits for the entitlement to be known; still-unknown fails open
        // (see waitForEntitlement).
        const entitled = await waitForEntitlement(billing, ENTITLEMENT_KEY);
        if (!entitled) {
          openUpgradeModal(billing, undefined, ENTITLEMENT_KEY);
          entitlementBlocked.value = true;
        } else {
          entitlementBlocked.value = false;
        }
      },
      { immediate: true }
    );

    const initialRunId = computed(() =>
      typeof route.query.runId === "string" ? route.query.runId : undefined
    );

    return {
      resolvedOrgId,
      entitlementBlocked,
      notFound,
      initialRunId,
    };
  },
});
</script>
